import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Eye, ArrowLeft, User } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(data);

      // SEO meta
      document.title = `${data.meta_title || data.title} | TesisKirala.com`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute('content', data.meta_description || data.excerpt || data.title);
      }

      // JSON-LD Article schema for Google Rich Results
      let script = document.getElementById('blog-article-jsonld');
      if (script) script.remove();
      script = document.createElement('script');
      script.id = 'blog-article-jsonld';
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: data.title,
        description: data.meta_description || data.excerpt,
        image: data.cover_image || 'https://tesiskirala.com/logo.webp',
        datePublished: data.published_at,
        dateModified: data.updated_at,
        author: { '@type': 'Organization', name: data.author_name || 'TesisKirala Editör' },
        publisher: {
          '@type': 'Organization',
          name: 'TesisKirala.com',
          logo: { '@type': 'ImageObject', url: 'https://tesiskirala.com/logo.webp' },
        },
        keywords: (data.keywords || []).join(', '),
      });
      document.head.appendChild(script);

      // Related posts
      const relRes = await axios.get(`${API}/blog/posts/related`, {
        params: { city: data.city, facility_type: data.facility_type, limit: 3 },
      });
      setRelated((relRes.data || []).filter(p => p.slug !== data.slug).slice(0, 3));
    } catch (e) {
      console.error('blog post error', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-32">
        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-3xl mx-auto py-32 px-4 text-center">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Yazı bulunamadı</h1>
        <p className="text-slate-600 mb-6">Aradığınız yazı silinmiş veya henüz yayınlanmamış olabilir.</p>
        <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">
          <ArrowLeft className="w-4 h-4" /> Tüm Yazılara Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Cover */}
      {post.cover_image && (
        <div className="w-full h-72 sm:h-96 bg-slate-200 overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-4 py-12">
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 mb-6"
          data-testid="blog-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Tüm Yazılar
        </button>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 leading-tight" data-testid="blog-post-title">
          {post.title}
        </h1>

        <div className="flex items-center flex-wrap gap-4 text-sm text-slate-500 mb-8 pb-6 border-b border-slate-200">
          <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {post.author_name || 'TesisKirala Editör'}</span>
          <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {formatDate(post.published_at)}</span>
          <span className="flex items-center gap-1.5"><Eye className="w-4 h-4" /> {post.view_count || 0} okunma</span>
        </div>

        {post.excerpt && (
          <p className="text-lg text-slate-700 italic mb-8 leading-relaxed">{post.excerpt}</p>
        )}

        {/* Content (HTML) */}
        <div
          className="prose prose-slate prose-lg max-w-none prose-headings:font-bold prose-headings:text-slate-900 prose-a:text-emerald-600 prose-strong:text-slate-900 prose-img:rounded-lg"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Keywords / Tags */}
        {post.keywords && post.keywords.length > 0 && (
          <div className="mt-10 pt-6 border-t border-slate-200">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Etiketler</div>
            <div className="flex flex-wrap gap-2">
              {post.keywords.map((k, i) => (
                <span key={i} className="px-3 py-1 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {k}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Related Posts */}
        {related.length > 0 && (
          <div className="mt-16 pt-10 border-t border-slate-200">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">İlgili Yazılar</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.post_id}
                  to={`/blog/${r.slug}`}
                  className="group bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`related-post-${r.slug}`}
                >
                  <div className="h-32 bg-gradient-to-br from-emerald-100 to-emerald-200 overflow-hidden">
                    {r.cover_image ? (
                      <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-emerald-700 text-3xl font-bold opacity-30">TK</div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-slate-900 group-hover:text-emerald-700 line-clamp-2">{r.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
